import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { ShieldCheck, AlertTriangle, HeartHandshake, Users, FileText, Mail } from "lucide-react";

/**
 * Tier 1 content (2026-07): public safeguarding policy. Written for the
 * audiences who look for this before anything else — GPs, link workers,
 * council officers and worried relatives.
 *
 * >>> POLICY COPY REQUIRES OWNER REVIEW BEFORE LAUNCH <<<
 * (tracked in memory/LAUNCH_CHECKLIST.md)
 */
const LAST_UPDATED = "July 2026";

function SectionBlock({ icon: Icon, title, children, testId }) {
  return (
    <section className="mt-10" data-testid={testId}>
      <h2 className="font-display text-2xl text-forest tracking-tight inline-flex items-center gap-2.5">
        <Icon className="w-5 h-5 text-terracotta shrink-0" /> {title}
      </h2>
      <div className="text-neutral-700 leading-relaxed mt-3 space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

export default function Safeguarding() {
  return (
    <div className="bg-cream min-h-screen">
      <Seo
        title="Safeguarding"
        description="How CivicHearth keeps neighbours safe: safer volunteering principles, trust levels, DBS attestations, mediated referrals and how to report a concern."
      />
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="safeguarding-page">
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Policy</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest mt-3 tracking-tight">Safeguarding</h1>
        <p className="text-neutral-600 mt-4 text-lg leading-relaxed">
          CivicHearth exists so neighbours can help neighbours. That only works if everyone —
          especially people who may be vulnerable — is kept safe while it happens. This page
          explains, in plain English, what we do and what we ask of you.
        </p>

        <SectionBlock icon={ShieldCheck} title="Our commitment and scope" testId="sg-commitment">
          <p>
            We are committed to protecting everyone who uses CivicHearth — volunteers, people
            asking for help, gathering attendees and the professionals who refer people to us —
            from harm, abuse, neglect and exploitation. This policy applies to everything
            arranged through the platform: volunteering opportunities, neighbour-to-neighbour
            help requests, collectives and their gatherings, and professional referrals.
          </p>
          <p>
            CivicHearth is a connecting platform, not a care provider. We do not carry out
            regulated care activity, and nothing here replaces the safeguarding duties of the
            organisations and professionals who use us.
          </p>
        </SectionBlock>

        <SectionBlock icon={Users} title="Safer volunteering principles" testId="sg-principles">
          <p>These rules are built into how the product works, not just written here:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><b>First meetings happen in shared spaces — never private homes.</b> Gatherings and first visits belong in community rooms, libraries, cafés and other public places.</li>
            <li><b>Helpers never handle passwords or make payments — they show, the owner does.</b> This applies to all digital help, shopping and errands.</li>
            <li><b>Check-in rounds are done in pairs, in daylight, with an agreed script.</b> Nobody knocks alone, and nobody enters a home on a first visit.</li>
            <li><b>Concerns go to the organiser, who escalates through the report path</b> — no one investigates on their own.</li>
          </ul>
        </SectionBlock>

        <SectionBlock icon={ShieldCheck} title="Trust levels and DBS records" testId="sg-trust">
          <p>
            Every volunteer has a server-computed <b>trust level</b> shown as a shield on their
            profile: from a new, email-verified neighbour up to a community-vouched volunteer
            endorsed by established members or vouched for by a collective leader.
          </p>
          <p>
            The highest level, <b>"DBS seen"</b>, means a verified organisation has told us
            they have <i>seen</i> a current DBS certificate for that volunteer. That is all it
            means: we record only the organisation's name and the date they saw it — <b>we
            never check, verify or store certificates</b> (there are no certificate numbers,
            scans or uploads anywhere in the product). Records show their age and can be
            withdrawn by the organisation at any time. As with everything on CivicHearth,
            always use your own judgement.
          </p>
          <p>
            People asking for help with sensitive tasks can require a minimum trust level
            before anyone can offer — and the person asking always chooses who helps.
          </p>
        </SectionBlock>

        <SectionBlock icon={HeartHandshake} title="Referrals for potentially vulnerable neighbours" testId="sg-referrals">
          <p>
            GPs, link workers and other local professionals can refer someone who needs
            practical help. These referrals are <b>mediated by the professional</b>: the
            professional posts the request, receives the offers and chooses the helper — the
            person being helped is never exposed to the platform unless they choose to join.
          </p>
          <p>
            We hold the minimum possible information about the person being helped: a first
            name and the first half of a postcode. No contact details, no health or medical
            information — the product has no fields for them and professionals are instructed
            to describe the practical task only. Every referral requires the professional to
            confirm they have the person's consent.
          </p>
        </SectionBlock>

        <SectionBlock icon={AlertTriangle} title="Reporting a concern" testId="sg-report">
          <p>
            If someone is in immediate danger, call <b>999</b> first.
          </p>
          <p>
            For anything else — behaviour that worries you, a listing that feels wrong, a
            concern about a volunteer or an organiser — use the <b>Report</b> option on any
            profile, opportunity or gathering, or start from our{" "}
            <Link to="/safety" data-testid="sg-safety-link" className="text-forest font-medium underline underline-offset-2">Safety page</Link>.
            Reports go straight to our moderation team.
          </p>
          <p>
            What happens next: we review every report, usually within one working day. We may
            pause listings or accounts while we look into it, contact those involved, and —
            where a safeguarding threshold is met — pass information to the local authority
            or the police. We tell the reporter when the matter is closed, within the limits
            of others' privacy.
          </p>
        </SectionBlock>

        <SectionBlock icon={Mail} title="Contact for safeguarding matters" testId="sg-contact">
          <p>
            For safeguarding questions, policies or partnership requirements (for example, a
            PCN or council due-diligence review), contact{" "}
            <a href="mailto:hello@civichearth.com?subject=Safeguarding" data-testid="sg-contact-email" className="text-forest font-medium underline underline-offset-2">
              hello@civichearth.com
            </a>. Professionals can read how referrals work on our{" "}
            <Link to="/for-professionals" className="text-forest font-medium underline underline-offset-2">For professionals</Link> page.
          </p>
        </SectionBlock>

        <div className="mt-12 pt-5 border-t border-forest/10 text-sm text-neutral-500 inline-flex items-center gap-2" data-testid="sg-reviewed-line">
          <FileText className="w-4 h-4" />
          This policy is reviewed regularly. Last updated: {LAST_UPDATED}.
        </div>
      </div>
    </div>
  );
}
